import { failure, isFailure, isOk, isPending, ok, pending, Result } from "@/common/common";
import { validateLocalDateInput } from "@/common/commonForms";
import {
  commandError,
  commentMaxLength,
  CompanyCustomField,
  CompanySystemField,
  CustomFieldCompanyScope,
  CustomFieldContact,
  CustomFieldContactRole,
  CustomFieldEventEntry,
  CustomFieldNumberFormat,
  CustomFieldSelectOption,
  CustomFieldValidations,
  CustomFieldValue,
  dataTypeLabels,
  numberDigitCount,
  requiredLengthMessage,
  systemFieldDescriptions,
  systemFieldDetailLinkUrl,
  SystemFieldDetails,
  SystemFieldKey,
  systemFieldLabels,
  SystemFieldLogEntry,
  valueText,
} from "@/customFields/customFieldsDomain";
import { customFieldsApiClient } from "@/customFields/customFieldsApiClient";
import type { Localized } from "@/companyRegistry/companyRegistryApiClient";
import { DCEvent, showGeneralError, updateStore, UpdateStoreEvent } from "@/events/eventsDomain";
import type { DialogProps } from "@/components-v2/Dialog";
import type { TabsProps } from "@/components-v2/Tabs";
import { Language } from "@/i18n/languages";
import { TMessage, TMessageParamDef } from "@/i18n/tt";
import { filterInputString, formatInputString, parseInputString } from "@/utils/numberInput";
import { companyFieldApiClient } from "./companyFieldApiClient";

export type CompanyFieldRef =
  | { source: "SYSTEM"; field: SystemFieldKey }
  | { source: "CUSTOM"; definitionId: string };

export type CompanyFieldFormat = Extract<
  TMessageParamDef,
  { type: "country" | "date" | "datetime" | "number" | "org-no" | "percent" | "time" }
>;

export type CompanyFieldInfo = {
  type: "string" | "number" | "date";
  format?: CompanyFieldFormat;
  asOf?: boolean;
} & (
  | { source: "SYSTEM"; field: SystemFieldKey }
  | {
      source: "CUSTOM";
      definitionId: string;
      accountId: string;
      accountName: string;
      name: string;
      description?: string;
    }
);

export const companyFieldLabel = (info: CompanyFieldInfo): TMessage => {
  switch (info.source) {
    case "SYSTEM":
      return systemFieldLabels[info.field];
    case "CUSTOM":
      return { translated: info.name };
  }
};

export const companyFieldDescription = (info: CompanyFieldInfo): TMessage | undefined => {
  switch (info.source) {
    case "SYSTEM":
      return systemFieldDescriptions[info.field];
    case "CUSTOM":
      return info.description ? { translated: info.description } : undefined;
  }
};

export const companyFieldRef = (info: CompanyFieldInfo): CompanyFieldRef => {
  switch (info.source) {
    case "SYSTEM":
      return { source: "SYSTEM", field: info.field };
    case "CUSTOM":
      return { source: "CUSTOM", definitionId: info.definitionId };
  }
};

export const sameCompanyFieldRef = (a: CompanyFieldRef, b: CompanyFieldRef): boolean => {
  switch (a.source) {
    case "SYSTEM":
      return b.source === "SYSTEM" && a.field === b.field;
    case "CUSTOM":
      return b.source === "CUSTOM" && a.definitionId === b.definitionId;
  }
};

export type CompanyFieldEditRule = "EDITABLE" | "READ_ONLY_REGISTER" | "CALCULATED";

export type CompanyFieldInputType =
  | "TEXT"
  | "NUMBER"
  | "DATE"
  | "SINGLE_SELECT"
  | "COUNTRY_CODE"
  | "CONTACT";

export type CompanyFieldConfig = {
  inputType: CompanyFieldInputType;
  selectOptions: CustomFieldSelectOption[];
  numberFormat?: CustomFieldNumberFormat;
  requiredLength?: number;
  limitToRole?: CustomFieldContactRole;
  validations: CustomFieldValidations;
  commentRequiredFor: string[];
};

export type CompanyFieldDetails = {
  orgNo: string;
  companyName: string;
  info: CompanyFieldInfo;
  value?: CustomFieldValue;
  valueLabel?: Localized;
  details?: SystemFieldDetails;
  isNotApplicable: boolean;
  effectiveFrom?: string;
  validThrough?: string;
  comment?: string;
  updatedAt?: string;
  updatedBy?: string;
  companyScope?: CustomFieldCompanyScope;
  config?: CompanyFieldConfig;
  editRule: CompanyFieldEditRule;
  canEdit: boolean;
  hasLog: boolean;
};

export type CompanyFieldCommand = {
  commandType: "SET_VALUE";
  field: CompanyFieldRef;
  value: CustomFieldValue | null;
  effectiveFrom?: string;
  validThrough?: string;
  comment?: string;
};

export type CompanyFieldLog =
  | { source: "SYSTEM"; entries: SystemFieldLogEntry[] }
  | { source: "CUSTOM"; entries: CustomFieldEventEntry[] };

export type CompanyFieldTab = "field" | "log";

export type CompanyFieldTarget = { orgNo: string; field: CompanyFieldRef; asOf?: string };

export type CompanyFieldDetailsState = CompanyFieldTarget & {
  tab: CompanyFieldTab;
  result: Result<CompanyFieldDetails>;
  logResult?: Result<CompanyFieldLog>;
};

export type CompanyFieldInput =
  | { type: "TEXT"; text: string }
  | { type: "NUMBER"; text: string }
  | { type: "DATE"; text: string }
  | { type: "SINGLE_SELECT"; optionId?: string }
  | { type: "COUNTRY_CODE"; code?: string }
  | {
      type: "CONTACT";
      contact?: CustomFieldContact;
      candidates: Result<CustomFieldContact[]>;
    };

export type CompanyFieldEditState = {
  orgNo: string;
  field: CompanyFieldRef;
  lang: Language;
  details: CompanyFieldDetails;
  config: CompanyFieldConfig;
  input: CompanyFieldInput;
  effectiveFrom: string;
  validThrough: string;
  comment: string;
  showErrors: boolean;
  submitResult?: Result<void, TMessage>;
};

export type CompanyFieldEditErrors = {
  value?: TMessage;
  effectiveFrom?: TMessage;
  validThrough?: TMessage;
  comment?: TMessage;
};

export type CompanyFieldSaved = (details: CompanyFieldDetails) => DCEvent[];

const isTarget = (state: CompanyFieldTarget, target: CompanyFieldTarget): boolean =>
  state.orgNo === target.orgNo &&
  sameCompanyFieldRef(state.field, target.field) &&
  state.asOf === target.asOf;

const updateDetails = (
  target: CompanyFieldTarget,
  partial: Partial<CompanyFieldDetailsState>,
): UpdateStoreEvent =>
  updateStore({
    companyFieldDetails: (state) =>
      state && isTarget(state, target) ? { ...state, ...partial } : state,
  });

const fetchDetails = (target: CompanyFieldTarget): DCEvent =>
  companyFieldApiClient.details(target.orgNo, target.field, target.asOf, {
    onSuccess: (res) => [updateDetails(target, { result: ok(res.data) })],
    onFailure: () => [updateDetails(target, { result: failure() })],
  });

const fetchLog = (target: CompanyFieldTarget): DCEvent => {
  const onFailure = () => [updateDetails(target, { logResult: failure() })];
  const field = target.field;
  switch (field.source) {
    case "SYSTEM":
      return customFieldsApiClient.systemFieldHistory(target.orgNo, field.field, {
        onSuccess: (res) => [
          updateDetails(target, { logResult: ok({ source: "SYSTEM", entries: res.data }) }),
        ],
        onFailure,
      });
    case "CUSTOM":
      return customFieldsApiClient.valueHistory(target.orgNo, field.definitionId, {
        onSuccess: (res) => [
          updateDetails(target, { logResult: ok({ source: "CUSTOM", entries: res.data }) }),
        ],
        onFailure,
      });
  }
};

export const openCompanyFieldDetails = (target: CompanyFieldTarget): DCEvent[] => [
  updateStore({ companyFieldDetails: { ...target, tab: "field", result: pending() } }),
  fetchDetails(target),
];

export const closeCompanyFieldDetails = (): DCEvent =>
  updateStore({ companyFieldDetails: undefined });

export const selectCompanyFieldTab = (
  state: CompanyFieldDetailsState,
  tab: CompanyFieldTab,
): DCEvent[] => {
  const loadsLog = tab === "log" && state.logResult === undefined;
  return [
    updateDetails(state, { tab, logResult: loadsLog ? pending() : state.logResult }),
    ...(loadsLog ? [fetchLog(state)] : []),
  ];
};

const updateEdit = (partial: Partial<CompanyFieldEditState>): UpdateStoreEvent =>
  updateStore({ companyFieldEdit: (edit) => edit && { ...edit, ...partial } });

const updateEditFor = (
  target: CompanyFieldTarget,
  update: (edit: CompanyFieldEditState) => CompanyFieldEditState,
): UpdateStoreEvent =>
  updateStore({
    companyFieldEdit: (edit) =>
      edit && edit.orgNo === target.orgNo && sameCompanyFieldRef(edit.field, target.field)
        ? update(edit)
        : edit,
  });

export const updateCompanyFieldEdit = (
  partial: Partial<
    Pick<CompanyFieldEditState, "input" | "effectiveFrom" | "validThrough" | "comment">
  >,
): DCEvent => updateEdit(partial);

export const closeCompanyFieldEdit = (): DCEvent => updateStore({ companyFieldEdit: undefined });

const numberOptions = (format?: CustomFieldNumberFormat) => ({
  isInteger: format?.decimalPlaces === 0,
  useGrouping: format?.separators !== "NONE",
  minDecimals: format?.decimalPlaces,
  maxDecimals: format?.decimalPlaces,
});

export const companyFieldNumberText = (
  text: string,
  config: CompanyFieldConfig,
  lang: Language,
): string =>
  filterInputString(text, lang, {
    maxDecimals: config.numberFormat?.decimalPlaces ?? 0,
    useGrouping: config.numberFormat?.separators !== "NONE",
    isNegative: true,
  });

const companyFieldInput = (
  config: CompanyFieldConfig,
  value: CustomFieldValue | undefined,
  lang: Language,
): CompanyFieldInput => {
  switch (config.inputType) {
    case "TEXT":
      return { type: "TEXT", text: value?.valueType === "TEXT" ? value.text : "" };
    case "NUMBER":
      return {
        type: "NUMBER",
        text:
          value?.valueType === "NUMBER"
            ? formatInputString(value.number, lang, numberOptions(config.numberFormat))
            : "",
      };
    case "DATE":
      return { type: "DATE", text: value?.valueType === "DATE" ? value.date : "" };
    case "SINGLE_SELECT":
      return {
        type: "SINGLE_SELECT",
        optionId: value?.valueType === "SINGLE_SELECT" ? value.optionId : undefined,
      };
    case "COUNTRY_CODE":
      return {
        type: "COUNTRY_CODE",
        code: value?.valueType === "COUNTRY_CODE" ? value.code : undefined,
      };
    case "CONTACT":
      return {
        type: "CONTACT",
        contact:
          value?.valueType === "CONTACT"
            ? { userId: value.userId, name: value.name, isInCompany: true, companyCount: 0 }
            : undefined,
        candidates: pending(),
      };
  }
};

export const companyFieldEditState = (
  details: CompanyFieldDetails,
  lang: Language,
): CompanyFieldEditState | undefined => {
  const config = details.config;
  if (!config || !details.canEdit) {
    return undefined;
  }
  return {
    orgNo: details.orgNo,
    field: companyFieldRef(details.info),
    lang,
    details,
    config,
    input: companyFieldInput(config, details.value, lang),
    effectiveFrom: details.info.source === "CUSTOM" ? (details.effectiveFrom ?? "") : "",
    validThrough: details.validThrough ?? "",
    comment: details.comment ?? "",
    showErrors: false,
  };
};

const fetchContacts = (edit: CompanyFieldEditState): DCEvent[] => {
  const field = edit.field;
  if (edit.input.type !== "CONTACT" || field.source !== "CUSTOM") {
    return [];
  }
  const setCandidates = (candidates: Result<CustomFieldContact[]>) =>
    updateEditFor(edit, (current) =>
      current.input.type === "CONTACT"
        ? { ...current, input: { ...current.input, candidates } }
        : current,
    );
  return [
    customFieldsApiClient.contactCandidates(edit.orgNo, field.definitionId, {
      onSuccess: (res) => [setCandidates(ok(res.data))],
      onFailure: () => [setCandidates(ok([]))],
    }),
  ];
};

const startEdit = (details: CompanyFieldDetails, lang: Language): DCEvent[] => {
  const edit = companyFieldEditState(details, lang);
  return edit ? [updateStore({ companyFieldEdit: edit }), ...fetchContacts(edit)] : [];
};

export const loadedCompanyFieldDetails = (
  state: CompanyFieldDetailsState | undefined,
  target: CompanyFieldTarget,
): CompanyFieldDetails | undefined =>
  state && isTarget(state, { ...target, asOf: undefined }) && isOk(state.result)
    ? state.result.data
    : undefined;

export const openCompanyFieldEdit = (target: {
  orgNo: string;
  field: CompanyFieldRef;
}): DCEvent => ({
  type: "with-store",
  events: (store) => {
    const loaded = loadedCompanyFieldDetails(store.companyFieldDetails, target);
    return loaded
      ? startEdit(loaded, store.i18n.lang)
      : [
          companyFieldApiClient.details(target.orgNo, target.field, undefined, {
            onSuccess: (res, state) => startEdit(res.data, state.i18n.lang),
            onFailure: () => [showGeneralError()],
          }),
        ];
  },
});

export const companyFieldValue = (
  edit: Pick<CompanyFieldEditState, "input" | "config" | "lang">,
): Result<CustomFieldValue | null, TMessage> => {
  const input = edit.input;
  switch (input.type) {
    case "TEXT": {
      const text = input.text.trim();
      return ok(text === "" ? null : { valueType: "TEXT", text });
    }
    case "NUMBER": {
      if (input.text.trim() === "") {
        return ok(null);
      }
      const number = parseInputString(input.text, edit.lang);
      if (number === undefined) {
        return failure({ key: "number-field.not-a-number" });
      }
      const requiredLength = edit.config.requiredLength;
      return requiredLength !== undefined &&
        numberDigitCount(number, edit.config.numberFormat) !== requiredLength
        ? failure(requiredLengthMessage(requiredLength))
        : ok({ valueType: "NUMBER", number });
    }
    case "DATE": {
      const error = validateLocalDateInput(input.text, {});
      if (error) {
        return failure(error);
      }
      return ok(input.text.trim() === "" ? null : { valueType: "DATE", date: input.text.trim() });
    }
    case "SINGLE_SELECT":
      return ok(input.optionId ? { valueType: "SINGLE_SELECT", optionId: input.optionId } : null);
    case "COUNTRY_CODE":
      return ok(input.code ? { valueType: "COUNTRY_CODE", code: input.code } : null);
    case "CONTACT":
      return ok(
        input.contact
          ? { valueType: "CONTACT", userId: input.contact.userId, name: input.contact.name }
          : null,
      );
  }
};

export const isCompanyFieldCommentRequired = (
  config: CompanyFieldConfig,
  value: CustomFieldValue | null,
): boolean => {
  switch (config.validations.COMMENT) {
    case "REQUIRED":
      return true;
    case "CONDITIONAL": {
      const text = valueText(config, value)?.toLowerCase();
      return (
        text !== undefined &&
        config.commentRequiredFor.some((trigger) => trigger.toLowerCase() === text)
      );
    }
    default:
      return false;
  }
};

const commentError = (
  edit: CompanyFieldEditState,
  value: CustomFieldValue,
): TMessage | undefined => {
  const comment = edit.comment.trim();
  if (!edit.config.validations.COMMENT) {
    return undefined;
  }
  if (comment.length > commentMaxLength) {
    return { key: "entity-data.comment-too-long", params: { max: commentMaxLength } };
  }
  return comment === "" && isCompanyFieldCommentRequired(edit.config, value)
    ? {
        key: "entity-data.comment-required",
        params: { value: valueText(edit.config, value) ?? "" },
      }
    : undefined;
};

const dateError = (
  edit: CompanyFieldEditState,
  validation: "EFFECTIVE_FROM_DATE" | "VALID_THROUGH_DATE",
): TMessage | undefined => {
  const requirement = edit.config.validations[validation];
  const text = validation === "EFFECTIVE_FROM_DATE" ? edit.effectiveFrom : edit.validThrough;
  return requirement
    ? validateLocalDateInput(text, { required: requirement === "REQUIRED" })
    : undefined;
};

export const validateCompanyFieldEdit = (edit: CompanyFieldEditState): CompanyFieldEditErrors => {
  const parsed = companyFieldValue(edit);
  if (isFailure(parsed)) {
    return { value: parsed.error };
  }
  if (!isOk(parsed) || parsed.data === null) {
    return {};
  }
  return {
    effectiveFrom: dateError(edit, "EFFECTIVE_FROM_DATE"),
    validThrough: dateError(edit, "VALID_THROUGH_DATE"),
    comment: commentError(edit, parsed.data),
  };
};

export const isCompanyFieldEditValid = (errors: CompanyFieldEditErrors): boolean =>
  Object.values(errors).every((error) => error === undefined);

const optionalText = (text: string): string | undefined => text.trim() || undefined;

export const toCompanyFieldCommand = (
  edit: CompanyFieldEditState,
): CompanyFieldCommand | undefined => {
  const parsed = companyFieldValue(edit);
  if (!isOk(parsed) || !isCompanyFieldEditValid(validateCompanyFieldEdit(edit))) {
    return undefined;
  }
  const value = parsed.data;
  const validations = edit.config.validations;
  return value === null
    ? { commandType: "SET_VALUE", field: edit.field, value }
    : {
        commandType: "SET_VALUE",
        field: edit.field,
        value,
        effectiveFrom: validations.EFFECTIVE_FROM_DATE
          ? optionalText(edit.effectiveFrom)
          : undefined,
        validThrough: validations.VALID_THROUGH_DATE ? optionalText(edit.validThrough) : undefined,
        comment: validations.COMMENT ? optionalText(edit.comment) : undefined,
      };
};

const refreshDetails = (
  state: CompanyFieldDetailsState | undefined,
  saved: CompanyFieldDetails,
): DCEvent[] => {
  if (
    !state ||
    state.orgNo !== saved.orgNo ||
    !sameCompanyFieldRef(state.field, companyFieldRef(saved.info))
  ) {
    return [];
  }
  const showsLog = state.tab === "log";
  return [
    updateDetails(state, {
      result: state.asOf === undefined ? ok(saved) : state.result,
      logResult: showsLog ? pending() : undefined,
    }),
    ...(state.asOf === undefined ? [] : [fetchDetails(state)]),
    ...(showsLog ? [fetchLog(state)] : []),
  ];
};

export const submitCompanyFieldEdit = (
  edit: CompanyFieldEditState,
  onSaved: CompanyFieldSaved,
): DCEvent[] => {
  const command = toCompanyFieldCommand(edit);
  if (!command) {
    return [updateEdit({ showErrors: true })];
  }
  return [
    updateEdit({ showErrors: true, submitResult: pending() }),
    companyFieldApiClient.command(edit.orgNo, command, {
      onSuccess: (res, state) => [
        ...refreshDetails(state.companyFieldDetails, res.data),
        closeCompanyFieldEdit(),
        ...onSaved(res.data),
      ],
      onFailure: (err) => [
        updateEditFor(edit, (current) => ({
          ...current,
          submitResult: failure(commandError(err)),
        })),
      ],
    }),
  ];
};

export const companyFieldOverlapWarning = (edit: CompanyFieldEditState): TMessage | undefined => {
  const parsed = companyFieldValue(edit);
  return isOk(parsed) && parsed.data !== null ? overlapWarningFor(edit, parsed.data) : undefined;
};

const overlapWarningFor = (
  edit: CompanyFieldEditState,
  value: CustomFieldValue,
): TMessage | undefined => {
  const effectiveFrom = optionalText(edit.effectiveFrom);
  const previousThrough = edit.details.validThrough;
  if (
    !edit.config.validations.EFFECTIVE_FROM_DATE ||
    !effectiveFrom ||
    effectiveFrom === edit.details.effectiveFrom ||
    !previousThrough ||
    effectiveFrom > previousThrough
  ) {
    return undefined;
  }
  return {
    key: "entity-data.overlap-warning",
    params: {
      from: { type: "date", value: effectiveFrom },
      through: { type: "date", value: previousThrough },
      value: valueText(edit.config, value) ?? "",
    },
  };
};

export const companyFieldContacts = (
  input: Extract<CompanyFieldInput, { type: "CONTACT" }>,
): CustomFieldContact[] => {
  const candidates = isOk(input.candidates) ? input.candidates.data : [];
  const selected = input.contact;
  return selected && !candidates.some((candidate) => candidate.userId === selected.userId)
    ? [selected, ...candidates]
    : candidates;
};

export const companyFieldEditTitle = (details: CompanyFieldDetails): TMessage => ({
  key: "entity-data.update-value-title",
  params: { field: companyFieldLabel(details.info), companyName: details.companyName },
});

export const companySystemField = (details: CompanyFieldDetails): CompanySystemField | undefined =>
  details.info.source === "SYSTEM"
    ? {
        systemField: details.info.field,
        value: details.value,
        details: details.details,
        valueLabel: details.valueLabel,
        isNotApplicable: details.isNotApplicable,
        effectiveFrom: details.effectiveFrom,
        updatedAt: details.updatedAt,
        updatedBy: details.updatedBy,
      }
    : undefined;

export const companyFieldDisplay = (
  details: CompanyFieldDetails,
): Pick<CompanyCustomField, "selectOptions" | "numberFormat"> => ({
  selectOptions: details.config?.selectOptions ?? [],
  numberFormat: details.config?.numberFormat,
});

const inputTypeLabels: { [key in CompanyFieldInputType]: TMessage } = {
  TEXT: dataTypeLabels.TEXT,
  NUMBER: dataTypeLabels.NUMBER,
  DATE: dataTypeLabels.DATE,
  SINGLE_SELECT: dataTypeLabels.SINGLE_SELECT,
  COUNTRY_CODE: dataTypeLabels.COUNTRY_CODE,
  CONTACT: dataTypeLabels.CONTACT,
};

export const companyFieldTypeLabel = (details: CompanyFieldDetails): TMessage =>
  details.info.source === "CUSTOM" && details.config
    ? inputTypeLabels[details.config.inputType]
    : { key: "entity-data.system-field" };

export const companyFieldSourceUrl = (details: CompanyFieldDetails): string | undefined => {
  const field = companySystemField(details);
  return field ? systemFieldDetailLinkUrl(field, details.orgNo) : undefined;
};

export const companyFieldSelectOptions = (
  config: CompanyFieldConfig,
  optionId?: string,
): CustomFieldSelectOption[] =>
  config.selectOptions.filter((option) => !option.isArchived || option.id === optionId);

export const prepCompanyFieldTabs = (
  state: CompanyFieldDetailsState,
  details: CompanyFieldDetails,
): TabsProps => ({
  tabs: [
    {
      id: "field",
      label: { key: "entity-data.field" },
      isActive: state.tab === "field",
      onSelect: selectCompanyFieldTab(state, "field"),
    },
    {
      id: "log",
      label: { key: "entity-data.tab-log" },
      isActive: state.tab === "log",
      disabled: !details.hasLog,
      onSelect: selectCompanyFieldTab(state, "log"),
    },
  ],
});

export const prepCompanyFieldEditDialog = (
  edit: CompanyFieldEditState,
  onSaved: CompanyFieldSaved,
): DialogProps => ({
  title: companyFieldEditTitle(edit.details),
  onCancel: [closeCompanyFieldEdit()],
  onSubmit: () => submitCompanyFieldEdit(edit, onSaved),
  isSubmitting: isPending(edit.submitResult),
  submitLabel: { key: "save" },
  maxWidth: "sm",
});
